import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'

export interface CompanySettings {
  id?: string
  company_name?: string
  company_address?: string
  company_nip?: string
  company_regon?: string
  company_bank_account?: string
  company_phone?: string
  company_email?: string
  company_representative_name?: string
  company_representative_position?: string
  company_logo_path?: string
  custom_menu_fee_enabled?: boolean
  custom_menu_fee_amount?: number
  custom_menu_fee_type?: 'per_person' | 'fixed'
  created_at?: string
  updated_at?: string
}

export function useCompanySettings() {
  const [settings, setSettings] = useState<CompanySettings | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      // Get the first (and only) record from company_settings
      const { data, error: fetchError } = await supabase
        .from('company_settings')
        .select('*')
        .limit(1)
        .single()

      if (fetchError) {
        // If no record exists, that's okay - return empty settings
        if (fetchError.code === 'PGRST116') {
          setSettings(null)
          return
        }
        throw fetchError
      }

      setSettings(data)
    } catch (err: unknown) {
      console.error('Error fetching company settings:', err)
      const errorMessage = err instanceof Error ? err.message : 'Błąd podczas pobierania danych firmy'
      setError(errorMessage)
      setSettings(null)
    } finally {
      setLoading(false)
    }
  }, [])

  const saveSettings = useCallback(async (newSettings: Partial<CompanySettings>) => {
    try {
      setSaving(true)
      setError(null)

      // Check if settings already exist
      const { data: existing } = await supabase
        .from('company_settings')
        .select('id')
        .limit(1)
        .single()

      if (existing) {
        const existingId = (existing as { id: string }).id
        // Update existing record
        const { data, error: updateError } = await supabase
          .from('company_settings')
          .update(newSettings as never)
          .eq('id', existingId)
          .select()
          .single()

        if (updateError) throw updateError
        setSettings(data)
      } else {
        // Create new record
        const { data, error: insertError } = await supabase
          .from('company_settings')
          .insert(newSettings as never)
          .select()
          .single()

        if (insertError) throw insertError
        setSettings(data)
      }
    } catch (err: unknown) {
      console.error('Error saving company settings:', err)
      const errorMessage = err instanceof Error ? err.message : 'Błąd podczas zapisywania danych firmy'
      setError(errorMessage)
      throw err
    } finally {
      setSaving(false)
    }
  }, [])

  useEffect(() => {
    fetchSettings()
  }, [fetchSettings])

  return {
    settings,
    loading,
    saving,
    error,
    fetchSettings,
    saveSettings,
  }
}


